import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CROWD_PAGINATION_DEFAULT_PAGE_SIZE,
  type CrowdPaginationEnvelope,
} from '../components/PaginationControls';

export interface LocalPaginationState<T> {
  pageRows: T[];
  pagination: CrowdPaginationEnvelope;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  resetPage: () => void;
}

export function useLocalPagination<T>(
  rows: readonly T[],
  initialPageSize = CROWD_PAGINATION_DEFAULT_PAGE_SIZE,
): LocalPaginationState<T> {
  const [page, setPageValue] = useState(1);
  const [pageSize, setPageSizeValue] = useState(initialPageSize);
  const total = rows.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  useEffect(() => {
    setPageValue(current => Math.min(Math.max(current, 1), pageCount));
  }, [pageCount]);

  const setPage = useCallback((nextPage: number) => {
    setPageValue(Math.max(1, Math.trunc(nextPage)));
  }, []);

  const setPageSize = useCallback((nextPageSize: number) => {
    setPageSizeValue(Math.max(1, Math.trunc(nextPageSize)));
    setPageValue(1);
  }, []);

  const resetPage = useCallback(() => {
    setPageValue(1);
  }, []);

  const pageRows = useMemo(() => {
    const offset = (page - 1) * pageSize;
    return rows.slice(offset, offset + pageSize);
  }, [page, pageSize, rows]);

  const pagination = useMemo<CrowdPaginationEnvelope>(() => ({
    total,
    page,
    pageSize,
    hasNextPage: page * pageSize < total,
  }), [page, pageSize, total]);

  return { pageRows, pagination, setPage, setPageSize, resetPage };
}
